import { useState } from "react";

import { QontyLoading } from "@components/QontyLoading";
import {
    NovedadesExportService,
    NovedadesExportServiceModel,
} from "@services/NovedadesExportService";
import { HollowedButton, NormalButton } from "@shared/designSystem/Button";
import { ComboBox, ComboBoxOption } from "@shared/designSystem/ComboBox";
import { QSwitch } from "@shared/designSystem/QSwitch";
import {
    HorizontalContainer,
    TableCell,
    TableHeaderCell,
    TableHeaderContainer,
    TableRowContainer,
    VerticalContainer,
} from "@shared/designSystem/Containers";
import { MilestoneSteps } from "@shared/designSystem/MilestoneSteps";
import { RadioButton } from "@shared/designSystem/RadioButton";
import { SmallText, Text } from "@shared/designSystem/Texts";
import designSystemStyles from "@shared/styles/designSystemStyles";

import { useExportNovedadesExportStepViewModel } from "./ExportNovedadesExportStepScreenViewModel";
import { Loader } from "@shared/designSystem/Loader";
import { SystemIcon } from "@shared/designSystem/SystemIcon";

interface ExportNovedadesExportStepScreenProps {
    process: NovedadesExportServiceModel.ProcessRecord;
    service: NovedadesExportService;
    onFileGenerationStarted?: () => void;
}

const systemExportMilestones = [
    { id: "PREPARING_EXPORT_DATA", title: "Preparación".localized() },
    { id: "DATA_VALIDATION", title: "Validación".localized() },
    { id: "FILE_GENERATION", title: "Generación".localized() },
    { id: "FILE_CREATED", title: "Archivo".localized() },
];

const systemMilestoneByStatus: Record<string, string> = {
    not_configured: "PREPARING_EXPORT_DATA",
    ready: "PREPARING_EXPORT_DATA",
    preparing_export_data: "PREPARING_EXPORT_DATA",
    data_validation: "DATA_VALIDATION",
    validation_failed: "DATA_VALIDATION",
    file_generation: "FILE_GENERATION",
    completed: "FILE_CREATED",
};

const colppyTipoOptions: ComboBoxOption[] = [
    { id: "P", title: "Primer quincena (P)".localized() },
    { id: "M", title: "Mes (M)".localized() },
    { id: "O", title: "Complementaria (O)".localized() },
    { id: "S", title: "SAC (S)".localized() },
    { id: "V", title: "Vacaciones (V)".localized() },
    { id: "F", title: "Liquidación final (F)".localized() },
];

const validationColumnWidths = {
    rowIndex: "10%",
    employeeID: "24%",
    concept: "22%",
    error: "44%",
};

interface RestartFileGenerationInletProps {
    isConfirming: boolean;
    onCancel: () => void;
    onConfirm: () => void;
}

const RestartFileGenerationInlet: React.FC<RestartFileGenerationInletProps> = ({
    isConfirming,
    onCancel,
    onConfirm,
}) => {
    return (
        <VerticalContainer
            gap={designSystemStyles.gapMd}
            margin="0"
            padding={designSystemStyles.paddingMd}
            backgroundColor={designSystemStyles.colorBackgroundGray3}
            borderRadius={designSystemStyles.cornerRadiusComponent}
        >
            <Text
                text={"¿Reiniciar la generación del archivo?".localized()}
                markdown={true}
            />
            <Text
                text={"Se eliminarán las filas de exportación actuales y se iniciará una nueva generación del archivo para el sistema seleccionado.".localized()}
                color={designSystemStyles.colorTextSecondary}
            />
            <HorizontalContainer justifyContent="flex-end" gap={designSystemStyles.gapSm}>
                <HollowedButton
                    text={"Cancelar".localized()}
                    onClick={onCancel}
                    disabled={isConfirming}
                    width="160px"
                />
                <NormalButton
                    text={"Reiniciar".localized()}
                    onClick={onConfirm}
                    loading={isConfirming}
                    disabled={isConfirming}
                    width="180px"
                />
            </HorizontalContainer>
        </VerticalContainer>
    );
};

const ExportNovedadesExportStepScreen: React.FC<ExportNovedadesExportStepScreenProps> = ({
    process,
    service,
    onFileGenerationStarted,
}) => {
    const [isRestartConfirmationOpen, setIsRestartConfirmationOpen] = useState(false);
    const viewModel = useExportNovedadesExportStepViewModel({
        process,
        service,
    });
    const systemProfileOptions: ComboBoxOption[] = viewModel.exportProfiles.map((profile) => ({
        id: profile.profileID,
        title: profile.label || profile.profileID,
    }));
    const selectedSystemProfileOption = systemProfileOptions.find((option) => (
        option.id === viewModel.selectedSystemProfileID
    ));

    const isDownloadButtonEnabled = viewModel.selectedTarget === "system"
        ? viewModel.canDownloadGeneratedSystemFile
        : viewModel.canExportToExcel;

    const isSystemExportInProgress =
        viewModel.systemExportStatus === "preparing_export_data" ||
        viewModel.systemExportStatus === "data_validation" ||
        viewModel.systemExportStatus === "file_generation";
    const isWorkedHoursToggleDisabled = viewModel.isExporting ||
        viewModel.isGeneratingSystemFile ||
        (viewModel.selectedTarget === "system" && isSystemExportInProgress);

    const startSystemFileGeneration = async () => {
        const didStart = await viewModel.generateSystemFile(isRestartConfirmationOpen);
        if (didStart) {
            setIsRestartConfirmationOpen(false);
            onFileGenerationStarted?.();
        }
    };

    const confirmRestartFileGeneration = () => {
        setIsRestartConfirmationOpen(true);
    };

    const renderSystemStatus = () => {
        if (viewModel.systemExportStatus === "not_configured") {
            return (
                <Text
                    text={"Esta exportación no tiene un sistema configurado.".localized()}
                    color={designSystemStyles.colorTextSecondary}
                />
            );
        }

        if (viewModel.systemExportStatus === "completed") {
            return (
                <VerticalContainer gap={designSystemStyles.gapLg} margin="0">
                    {process.outputFilename && (
                        <HorizontalContainer>
                            <SystemIcon type="notes" color={designSystemStyles.colorTextSecondary} />
                            <Text
                                text={process.outputFilename}
                                color={designSystemStyles.colorTextSecondary}
                            />
                        </HorizontalContainer>
                    )}
                    <HorizontalContainer>
                        <HollowedButton
                            text={"Generar nuevo archivo".localized()}
                            onClick={confirmRestartFileGeneration}
                            disabled={viewModel.isGeneratingSystemFile || isRestartConfirmationOpen}
                            width="220px"
                        />
                        <NormalButton
                            text={"Descargar archivo".localized()}
                            onClick={() => viewModel.downloadGeneratedSystemFile()}
                            disabled={!isDownloadButtonEnabled || isRestartConfirmationOpen}
                            width="220px"
                        />
                    </HorizontalContainer>
                    {isRestartConfirmationOpen && (
                        <RestartFileGenerationInlet
                            isConfirming={viewModel.isGeneratingSystemFile}
                            onCancel={() => setIsRestartConfirmationOpen(false)}
                            onConfirm={() => void startSystemFileGeneration()}
                        />
                    )}
                </VerticalContainer>
            );
        }

        if (viewModel.systemExportStatus === "validation_failed") {
            return (
                <VerticalContainer gap={designSystemStyles.gapLg} margin="0">
                    <MilestoneSteps
                        ariaLabel={"Progreso de exportación a sistema".localized()}
                        milestones={systemExportMilestones}
                        currentMilestoneId={systemMilestoneByStatus[viewModel.systemExportStatus]}
                        description={"La validación encontró errores antes de generar el archivo.".localized()}
                    />
                    {viewModel.isLoadingValidationRows ? (
                        <QontyLoading
                            text={"Cargando errores de validación...".localized()}
                            withQonty={false}
                        />
                    ) : (
                        <div style={{ display: "flex", flexDirection: "column", height: "100%", minHeight: 0, width: "100%", gap: designSystemStyles.gapLg }}>
                            <div style={{ display: "flex", flexDirection: "column", gap: designSystemStyles.gapLg, overflowY: "auto", flex: 1, minHeight: 0, width: "100%", paddingTop: designSystemStyles.paddingMd }}>                        
                                <VerticalContainer gap={designSystemStyles.gapNone} margin="0">
                                    <TableHeaderContainer height={designSystemStyles.sizeRowMedium}>
                                        <TableHeaderCell text={"Fila".localized()} width={validationColumnWidths.rowIndex} />
                                        <TableHeaderCell text={"Concepto".localized()} width={validationColumnWidths.concept} />
                                        <TableHeaderCell text={"Error".localized()} width={validationColumnWidths.error} />
                                    </TableHeaderContainer>
                                    <div style={{ display: "flex", flexDirection: "column", overflowY: "auto", maxHeight: "260px", width: "100%" }}>
                                        {viewModel.validationRows.length === 0 ? (
                                            <Text
                                                text={"No se encontraron detalles de los errores.".localized()}
                                                color={designSystemStyles.colorTextSecondary}
                                            />
                                        ) : (
                                            viewModel.validationRows.map((row) => (
                                                <TableRowContainer key={row.id}>
                                                    <TableCell text={row.rowIndex} width={validationColumnWidths.rowIndex} truncate={true} />
                                                    <TableCell text={row.concept} width={validationColumnWidths.concept} truncate={true} />
                                                    <TableCell text={row.error} width={validationColumnWidths.error} truncate={true} />
                                                </TableRowContainer>
                                            ))
                                        )}
                                    </div>
                                </VerticalContainer>
                            </div>
                            <HorizontalContainer justifyContent="flex-end" gap={designSystemStyles.gapSm} width="100%">
                                {isRestartConfirmationOpen ? (
                                    <RestartFileGenerationInlet
                                        isConfirming={viewModel.isGeneratingSystemFile}
                                        onCancel={() => setIsRestartConfirmationOpen(false)}
                                        onConfirm={() => void startSystemFileGeneration()}
                                    />
                                ) : (
                                    <HollowedButton
                                        text={"Reiniciar procesamiento".localized()}
                                        onClick={confirmRestartFileGeneration}
                                        disabled={viewModel.isGeneratingSystemFile}
                                        width="220px"
                                    />
                                )}
                            </HorizontalContainer>
                        </div>
                    )}
                </VerticalContainer>
            );
        }

        if (
            viewModel.systemExportStatus === "preparing_export_data" ||
            viewModel.systemExportStatus === "data_validation" ||
            viewModel.systemExportStatus === "file_generation"
        ) {
            return (
                <VerticalContainer alignment="center">
                    <MilestoneSteps
                        ariaLabel={"Progreso de exportación a sistema".localized()}
                        milestones={systemExportMilestones}
                        currentMilestoneId={systemMilestoneByStatus[viewModel.systemExportStatus]}
                        description={"Preparando y generando el archivo para el sistema.".localized()}
                    />
                    <Loader/>
                </VerticalContainer>
            );
        }

        return (
            <>
                <Text
                    text={"Se generará el archivo compatible con el sistema configurado para esta exportación.".localized()}
                    color={designSystemStyles.colorTextSecondary}
                />
                <NormalButton
                    text={"Generar archivo".localized()}
                    onClick={() => void startSystemFileGeneration()}
                    disabled={!viewModel.canGenerateSystemFile}
                    loading={viewModel.isGeneratingSystemFile}
                    width="220px"
                />
            </>
        );
    };

    return (
        <VerticalContainer
            height="100%"
            gap={designSystemStyles.gapLg}
            margin="0"
            paddingTop={designSystemStyles.paddingMd}
        >

            <VerticalContainer gap={designSystemStyles.gapXxxSm} margin="0">
                <HorizontalContainer gap={designSystemStyles.gapXxxSm} alignment="center">
                    <Text text={"Incluir cálculo de horas".localized()} />
                    <QSwitch
                        checked={viewModel.includeWorkedHours}
                        onChange={() => viewModel.setIncludeWorkedHours(!viewModel.includeWorkedHours)}
                        disabled={isWorkedHoursToggleDisabled}
                    />
                </HorizontalContainer>
            </VerticalContainer>

            <HorizontalContainer justifyContent="space-between" width="100%">
                <div role="radiogroup">
                    <HorizontalContainer gap={designSystemStyles.gapLg}>
                        <RadioButton
                            text={"Exportar a Excel".localized()}
                            selected={viewModel.selectedTarget === "excel"}
                            onClick={() => {
                                setIsRestartConfirmationOpen(false);
                                viewModel.setSelectedTarget("excel");
                            }}
                        />
                        <RadioButton
                            text={"Exportar a otro sistema".localized()}
                            selected={viewModel.selectedTarget === "system"}
                            onClick={() => {
                                setIsRestartConfirmationOpen(false);
                                viewModel.setSelectedTarget("system");
                            }}
                        />
                    </HorizontalContainer>
                </div>
            </HorizontalContainer>

            {viewModel.selectedTarget === "excel" ? (
                <VerticalContainer
                    gap={designSystemStyles.gapLg}
                    margin="0"
                    paddingTop={designSystemStyles.paddingMd}
                    borderRadius={designSystemStyles.cornerRadiusComponent}
                >
                    <Text
                        text={"Se generará un archivo con las mismas columnas del paso Colaboradores.".localized()}
                        color={designSystemStyles.colorTextSecondary}
                    />
                    <NormalButton
                        text={"Descargar Excel".localized()}
                        onClick={() => void viewModel.exportToExcel()}
                        disabled={!viewModel.canExportToExcel}
                        loading={viewModel.isExporting}
                        width="220px"
                    />
                    {viewModel.error && (
                        <Text text={viewModel.error} color={designSystemStyles.colorError} />
                    )}
                </VerticalContainer>
            ) : (
                <VerticalContainer
                    gap={designSystemStyles.gapLg}
                    margin="0"
                    paddingTop={designSystemStyles.paddingMd}
                    borderRadius={designSystemStyles.cornerRadiusComponent}
                    height="100%"
                >
                    {viewModel.isLoadingExportProfilesForFirstTime ? (
                        <QontyLoading
                            text={"Cargando perfiles de exportación...".localized()}
                            withQonty={false}
                        />
                    ) : (
                        <>
                            {systemProfileOptions.length > 0 && (
                                <VerticalContainer gap={designSystemStyles.gapXxxSm} width="360px" margin="0">
                                    <SmallText text={"Perfil de exportación".localized()} />
                                    <ComboBox
                                        options={systemProfileOptions}
                                        value={selectedSystemProfileOption}
                                        placeholder={"Seleccionar perfil".localized()}
                                        onChange={(option) => viewModel.setSelectedSystemProfileID(option.id)}
                                        maxVisibleOptions={5}
                                        iconType={undefined}
                                    />
                                </VerticalContainer>
                            )}
                            {viewModel.isColppyTipoSelectionVisible && (
                                <VerticalContainer gap={designSystemStyles.gapXxxSm} width="360px" margin="0">
                                    <SmallText text={"Tipo de liquidación".localized()} />
                                    <ComboBox
                                        options={colppyTipoOptions}
                                        value={colppyTipoOptions.find((option) => (
                                            option.id === viewModel.selectedColppyTipo
                                        ))}
                                        placeholder={"Seleccionar tipo de liquidación".localized()}
                                        onChange={(option) => viewModel.setSelectedColppyTipo(option.id)}
                                        maxVisibleOptions={6}
                                        iconType={undefined}
                                    />
                                </VerticalContainer>
                            )}
                            {viewModel.exportProfilesError && (
                                <Text text={viewModel.exportProfilesError} color={designSystemStyles.colorError} />
                            )}
                            {renderSystemStatus()}
                            {viewModel.systemExportMessage && (
                                <Text text={viewModel.systemExportMessage} color={designSystemStyles.colorTextSecondary} />
                            )}
                            {viewModel.error && (
                                <Text text={viewModel.error} color={designSystemStyles.colorError} />
                            )}
                        </>
                    )}
                </VerticalContainer>
            )}
        </VerticalContainer>
    );
};

export default ExportNovedadesExportStepScreen;
